/**
 * Boot-time env contract (plan §1.1 rule 4 · §6.8.11).
 *
 * Development/test default to local + auth disabled.
 * Production has **no defaults** and refuses local data and disabled auth.
 */
import { z } from 'zod';

import { SYNTHETIC_PROFILES, type SyntheticProfile } from '@meridian/core';

export type DataSourceMode = 'local' | 'http';
export type AuthMode = 'enabled' | 'disabled';
export type TaxonomySwitch = 'on' | 'off';

/**
 * Which credential paths the login panel offers (AD-15).
 *
 * `bootstrap` registers the first passkey and hides the password form;
 * `authenticate` is production steady state and hides the enrolment fields;
 * `auto` shows both and is the non-production fallback. Production refuses
 * `auto` (unset, empty, unknown, or the value itself) — accepting it would
 * ship handle fields and the enrolment fallback in a production bundle.
 *
 * Tuple-first (naming §3.9), like `SYNTHETIC_PROFILES` below: the members must
 * be a runtime iterable, a type and a Zod source at once. A hand-written union
 * beside independent `z.enum` literals lets a fourth mode compile while being
 * rejected at runtime, and lets the production subset silently omit it.
 */
export const PASSKEY_MODES = ['auto', 'bootstrap', 'authenticate'] as const;
export type PasskeyMode = (typeof PASSKEY_MODES)[number];

/**
 * Discriminated on `dataSource` so local + null profile is unrepresentable.
 * HTTP may still carry a non-null profile in non-production (unused by the
 * repository factory) or null in production.
 */
export type AppConfig =
  | {
      readonly dataSource: 'local';
      readonly syntheticProfile: SyntheticProfile;
      readonly authMode: AuthMode;
      readonly passkeyMode: PasskeyMode;
      readonly isProduction: boolean;
      readonly apiBaseUrl: string;
      readonly taxonomySwitch: TaxonomySwitch;
    }
  | {
      readonly dataSource: 'http';
      readonly syntheticProfile: SyntheticProfile | null;
      readonly authMode: AuthMode;
      readonly passkeyMode: PasskeyMode;
      readonly isProduction: boolean;
      readonly apiBaseUrl: string;
      readonly taxonomySwitch: TaxonomySwitch;
    };

/**
 * Every `VITE_*` key `parseConfig` reads, as one enumerable source.
 *
 * `loadConfig` below still names each key LITERALLY and must keep doing so —
 * Vite performs a textual replacement of `import.meta.env.VITE_FOO` at build
 * time and does not substitute `import.meta.env[key]`, so mapping over this
 * tuple there would ship `undefined` for every variable. Its consumer is the
 * deploy-time assert CLI, which runs under Node where dynamic access is fine,
 * and where a `Record<ViteEnvKey, …>` makes a forgotten key a COMPILE error
 * instead of a silent exit 0 (config/assert-production-config.ts).
 */
export const VITE_ENV_KEYS = [
  'VITE_DATA_SOURCE',
  'VITE_AUTH_MODE',
  'VITE_PASSKEY_MODE',
  'VITE_API_BASE_URL',
  'VITE_SYNTHETIC_PROFILE',
  'VITE_TAXONOMY_SWITCH',
] as const;

export type ViteEnvKey = (typeof VITE_ENV_KEYS)[number];

export interface RawEnv extends Partial<Readonly<Record<ViteEnvKey, string | undefined>>> {
  readonly MODE?: string | undefined;
  readonly PROD?: boolean | undefined;
  readonly DEV?: boolean | undefined;
}

const DataSourceSchema = z.enum(['local', 'http']);
const AuthModeSchema = z.enum(['enabled', 'disabled']);
const TaxonomySwitchSchema = z.enum(['on', 'off']);
const PasskeyModeSchema = z.enum(PASSKEY_MODES);
/* Derived by exclusion, not restated: production names what it REFUSES, so a
   new mode is offered there by default rather than silently dropped. */
const ProductionPasskeyModeSchema = PasskeyModeSchema.exclude(['auto']);
const SyntheticProfileSchema = z.enum(SYNTHETIC_PROFILES);

function trimOrUndefined(v: string | undefined): string | undefined {
  if (v === undefined) return undefined;
  const t = v.trim();
  return t.length === 0 ? '' : t;
}

interface EnumFieldOpts<T extends string> {
  readonly raw: string | undefined;
  readonly schema: z.ZodType<T>;
  readonly name: string;
  readonly allowed: string;
  readonly isProduction: boolean;
  readonly fallback: T;
}

function parseEnumField<T extends string>(opts: EnumFieldOpts<T>): T {
  const { raw, schema, name, allowed, isProduction, fallback } = opts;
  if (raw === undefined) {
    if (isProduction) {
      throw new Error(`${name} is required in production (must be ${allowed})`);
    }
    return fallback;
  }
  if (raw === '') {
    throw new Error(`${name} must be ${allowed} (empty string is invalid)`);
  }
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`${name} must be ${allowed}`);
  }
  return parsed.data;
}

function parsePasskeyMode(raw: string | undefined, isProduction: boolean): PasskeyMode {
  return parseEnumField({
    raw,
    schema: isProduction ? ProductionPasskeyModeSchema : PasskeyModeSchema,
    name: 'VITE_PASSKEY_MODE',
    allowed: isProduction
      ? '"bootstrap" or "authenticate"'
      : '"auto", "bootstrap" or "authenticate"',
    isProduction,
    fallback: 'auto',
  });
}

function parseTaxonomySwitch(raw: string | undefined, isProduction: boolean): TaxonomySwitch {
  return parseEnumField({
    raw,
    schema: TaxonomySwitchSchema,
    name: 'VITE_TAXONOMY_SWITCH',
    allowed: '"on" or "off"',
    isProduction,
    fallback: 'on',
  });
}

/**
 * REFUSED in production rather than defaulted, the same posture as
 * `VITE_DATA_SOURCE=local`: production serves http, so a synthetic profile set
 * there is a misconfiguration whose only honest report is a boot failure.
 *
 * Non-production defaults to `live-shaped` (plan §1.2 · §4.7) — local mode
 * exists to exercise PRODUCTION field shape, and a demo-fidelity default means
 * the screens first meet a null fundamental, an absent consensus or an
 * insufficient-history row in production.
 */
function parseSyntheticProfile(
  raw: string | undefined,
  isProduction: boolean,
): SyntheticProfile | null {
  if (isProduction) {
    if (raw !== undefined) {
      throw new Error(
        'VITE_SYNTHETIC_PROFILE is refused in production (http serves no synthetic data)',
      );
    }
    return null;
  }
  /* The type argument is PINNED, not inferred. `parseEnumField<T extends
     string>` collects inference candidates from every position including this
     function's return type, so the enclosing `SyntheticProfile | null`
     contributes a candidate that fails `T extends string`; TS then falls back
     to the constraint and the call resolves to `string`. The sibling calls in
     `parseConfig` never hit this because their targets are non-nullable. */
  return parseEnumField<SyntheticProfile>({
    raw,
    schema: SyntheticProfileSchema,
    name: 'VITE_SYNTHETIC_PROFILE',
    allowed: SYNTHETIC_PROFILES.map((p) => JSON.stringify(p)).join(' or '),
    isProduction,
    fallback: 'live-shaped',
  });
}

export function parseConfig(env: RawEnv): AppConfig {
  const isProduction = env.PROD === true || env.MODE === 'production';
  const dataSource = parseEnumField({
    raw: trimOrUndefined(env.VITE_DATA_SOURCE),
    schema: DataSourceSchema,
    name: 'VITE_DATA_SOURCE',
    allowed: '"local" or "http"',
    isProduction,
    fallback: 'local',
  });
  const authMode = parseEnumField({
    raw: trimOrUndefined(env.VITE_AUTH_MODE),
    schema: AuthModeSchema,
    name: 'VITE_AUTH_MODE',
    allowed: '"enabled" or "disabled"',
    isProduction,
    fallback: 'disabled',
  });

  if (isProduction && dataSource !== 'http') {
    throw new Error('VITE_DATA_SOURCE=local is refused in production (must be "http")');
  }
  if (isProduction && authMode === 'disabled') {
    throw new Error('VITE_AUTH_MODE=disabled is refused in production');
  }

  const rawBase = trimOrUndefined(env.VITE_API_BASE_URL);
  const apiBaseUrl = resolveApiBaseUrl(rawBase, isProduction);
  if (authMode === 'enabled' && isHttpUrl(apiBaseUrl)) {
    throw new Error('VITE_API_BASE_URL must be same-origin while VITE_AUTH_MODE=enabled');
  }

  const syntheticProfile = parseSyntheticProfile(
    trimOrUndefined(env.VITE_SYNTHETIC_PROFILE),
    isProduction,
  );

  /*
   * PLACEMENT IS LOAD-BEARING, twice over (AD-15).
   *
   * It must come after the two production refusal checks above: a production
   * build with `VITE_DATA_SOURCE=local` or `VITE_AUTH_MODE=disabled` has to
   * keep naming THAT variable. Parsing here rather than beside the other enum
   * fields also preserves the existing precedence of the VITE_SYNTHETIC_PROFILE
   * and VITE_API_BASE_URL refusals, so adding a required variable does not
   * silently rename every production misconfiguration report.
   *
   * Production is required with NO fallback, and the value `auto` is refused
   * there too. Defaulting to `auto` — or accepting an explicit one — would
   * reinstate the dead bootstrap fields this variable exists to close.
   */
  const passkeyMode = parsePasskeyMode(trimOrUndefined(env.VITE_PASSKEY_MODE), isProduction);

  /*
   * LAST, after passkey (AD-15 / area-taxonomy). Production is required with
   * no fallback — the same posture as VITE_DATA_SOURCE. Placing it here keeps
   * every existing production misconfiguration named for the variable that
   * was already refused, instead of renaming them to VITE_TAXONOMY_SWITCH.
   * Non-production defaults to 'on' so local and CI see the Classification
   * group; production unset throws.
   */
  const taxonomySwitch = parseTaxonomySwitch(
    trimOrUndefined(env.VITE_TAXONOMY_SWITCH),
    isProduction,
  );

  return freezeConfig({
    dataSource,
    authMode,
    passkeyMode,
    isProduction,
    apiBaseUrl,
    syntheticProfile,
    taxonomySwitch,
  });
}

/** Narrow by dataSource so local always carries a non-null profile. */
function freezeConfig(parts: {
  readonly dataSource: DataSourceMode;
  readonly authMode: AuthMode;
  readonly passkeyMode: PasskeyMode;
  readonly isProduction: boolean;
  readonly apiBaseUrl: string;
  readonly syntheticProfile: SyntheticProfile | null;
  readonly taxonomySwitch: TaxonomySwitch;
}): AppConfig {
  const {
    dataSource,
    authMode,
    passkeyMode,
    isProduction,
    apiBaseUrl,
    syntheticProfile,
    taxonomySwitch,
  } = parts;
  if (dataSource === 'local') {
    /*
     * NARROWING, not defence. No input reaches this throw — `parseSyntheticProfile`
     * returns null only in production, and `parseEnumField` already refuses
     * `VITE_DATA_SOURCE=local` there — so it is unreachable via `parseConfig`
     * and shows as an uncovered line. It stays because it is what proves
     * `syntheticProfile` non-null for the discriminated `'local'` arm below;
     * the alternative is an `as` cast, which style §5 bans outright.
     */
    if (syntheticProfile === null) {
      throw new Error('VITE_SYNTHETIC_PROFILE is required when VITE_DATA_SOURCE=local');
    }
    return Object.freeze({
      dataSource,
      authMode,
      passkeyMode,
      isProduction,
      apiBaseUrl,
      syntheticProfile,
      taxonomySwitch,
    });
  }
  return Object.freeze({
    dataSource,
    authMode,
    passkeyMode,
    isProduction,
    apiBaseUrl,
    syntheticProfile,
    taxonomySwitch,
  });
}

function isHttpUrl(base: string): boolean {
  return base.startsWith('http://') || base.startsWith('https://');
}

function isUnsafeApiBase(base: string): boolean {
  return (
    base.startsWith('//') ||
    base.includes('\\') ||
    base.startsWith('javascript:') ||
    base.startsWith('data:')
  );
}

function isProductionApiPath(base: string): boolean {
  const origin = 'https://meridian.invalid';
  const resolved = new URL(base, origin);
  return resolved.origin === origin && resolved.search === '' && resolved.hash === '';
}

function resolveDevelopmentApiBase(base: string): string {
  if (base.startsWith('/') || isHttpUrl(base)) return base;
  throw new Error('VITE_API_BASE_URL must be a relative path or http(s) URL');
}

/** Relative same-origin path in production; absolute http(s) allowed only in dev/test. */
function resolveApiBaseUrl(rawBase: string | undefined, isProduction: boolean): string {
  const base = rawBase === undefined || rawBase === '' ? '/api/v1' : rawBase.replace(/\/$/, '');
  if (isUnsafeApiBase(base)) {
    throw new Error('VITE_API_BASE_URL must be a relative path or http(s) URL');
  }
  if (isProduction) {
    if (!isProductionApiPath(base)) {
      throw new Error(
        'VITE_API_BASE_URL must be a same-origin relative path in production (e.g. "/api/v1")',
      );
    }
    return new URL(base, 'https://meridian.invalid').pathname.replace(/\/$/, '') || '/';
  }
  return resolveDevelopmentApiBase(base);
}

export function loadConfig(): AppConfig {
  return parseConfig({
    MODE: import.meta.env.MODE,
    PROD: import.meta.env.PROD,
    DEV: import.meta.env.DEV,
    VITE_DATA_SOURCE: import.meta.env.VITE_DATA_SOURCE,
    VITE_AUTH_MODE: import.meta.env.VITE_AUTH_MODE,
    VITE_PASSKEY_MODE: import.meta.env.VITE_PASSKEY_MODE,
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_SYNTHETIC_PROFILE: import.meta.env.VITE_SYNTHETIC_PROFILE,
    VITE_TAXONOMY_SWITCH: import.meta.env.VITE_TAXONOMY_SWITCH,
  });
}
